import { useCallback, useContext } from 'react';
import isEqual from 'lodash.isequal';

import { SelectContext } from '../context/SelectProvider';
import {
  getAllSelectableOptions,
  getFixedOptions,
  getSelectedValues,
} from '../../helpers';
import { SelectOption } from '../../types';

import styles from './useKeyNavigation.module.scss';

const useKeyNavigation = () => {
  const {
    multiple,
    selectedOptions,
    setIsVisible,
    isVisible,
    onSelect,
    closeOnSelect,
    options,
    optionsRef,
    selectedOptionsWithValuesOnly,
    showSelectAll,
    filteredOptions,
    focusedOptionRef,
    submitRef,
  } = useContext(SelectContext);

  const removeFocusClass = useCallback(() => {
    if (
      focusedOptionRef.current !== null &&
      optionsRef[focusedOptionRef.current]
    ) {
      optionsRef[focusedOptionRef.current].current?.classList.remove(
        styles.focused,
      );
    }
  }, [focusedOptionRef, optionsRef]);

  const addFocusClass = useCallback(
    (skipScroll?: boolean) => {
      if (focusedOptionRef.current !== null) {
        if (focusedOptionRef.current < 0) {
          focusedOptionRef.current = null;
          return;
        }

        if (optionsRef[focusedOptionRef.current]) {
          optionsRef[focusedOptionRef.current].current?.classList.add(
            styles.focused,
          );
          if (!skipScroll) {
            optionsRef[focusedOptionRef.current].current?.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            });
          }
        }
      }
    },
    [focusedOptionRef, optionsRef],
  );

  const handleSelectAll = useCallback(
    (selected: boolean, selectedWithKey = false) => {
      const opts = selected
        ? getAllSelectableOptions(options, selectedOptions)
        : getFixedOptions(options, selectedOptions);
      onSelect(opts);

      if (focusedOptionRef.current !== 0) {
        removeFocusClass();
        focusedOptionRef.current = 0;
        if (selectedWithKey) addFocusClass(true);
      }
    },
    [
      addFocusClass,
      focusedOptionRef,
      onSelect,
      options,
      removeFocusClass,
      selectedOptions,
    ],
  );

  const handleSelect = useCallback(
    (option: SelectOption, selectedWithKey = false) => {
      onSelect(
        getSelectedValues<SelectOption>({
          option,
          selectedOptions,
          selectedOptionsWithValuesOnly: selectedOptionsWithValuesOnly || [],
          multiple,
        }),
      );

      if (closeOnSelect && !multiple) setIsVisible(false);

      const newIndex =
        filteredOptions.findIndex((o) => o.value === option.value) +
        (showSelectAll ? 1 : 0);

      if (focusedOptionRef.current !== newIndex) {
        removeFocusClass();
        focusedOptionRef.current = newIndex;
        if (selectedWithKey) addFocusClass(true);
      }
    },
    [
      addFocusClass,
      closeOnSelect,
      filteredOptions,
      focusedOptionRef,
      multiple,
      onSelect,
      removeFocusClass,
      selectedOptions,
      selectedOptionsWithValuesOnly,
      setIsVisible,
      showSelectAll,
    ],
  );

  const handleArrowKey = useCallback(
    (direction: React.KeyboardEvent<HTMLElement>['key']) => {
      if (!optionsRef.length) {
        return;
      }

      if (focusedOptionRef.current === null) {
        const firstValidOption = optionsRef
          .slice()
          .find((el) => !el.current?.ariaDisabled);

        focusedOptionRef.current = optionsRef.findIndex(
          (opt) => opt === firstValidOption,
        );
        addFocusClass();
        return;
      }

      let nextIndex: number | null = null;
      let nextValidOption: React.RefObject<HTMLLIElement> | undefined;
      const reversedArray = optionsRef.slice().reverse();

      switch (direction) {
        case 'ArrowDown':
          removeFocusClass();

          nextIndex = focusedOptionRef.current + 1;

          nextValidOption = optionsRef
            .slice(nextIndex)
            .concat(optionsRef.slice(0, nextIndex))
            .find((el) => !el.current?.ariaDisabled);

          focusedOptionRef.current = optionsRef.findIndex(
            (opt) => opt === nextValidOption,
          );
          addFocusClass();
          break;

        case 'ArrowUp':
          removeFocusClass();

          nextIndex = optionsRef.length - focusedOptionRef.current;

          nextValidOption = reversedArray
            .slice(nextIndex)
            .concat(reversedArray.slice(0, nextIndex))
            .find((el) => !el.current?.ariaDisabled);

          focusedOptionRef.current = optionsRef.findIndex(
            (opt) => opt === nextValidOption,
          );

          addFocusClass();
          break;

        default:
          break;
      }
    },
    [optionsRef, focusedOptionRef, addFocusClass, removeFocusClass],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();

          if (showSelectAll && focusedOptionRef.current === 0) {
            handleSelectAll(!isEqual(filteredOptions, selectedOptions), true);
            break;
          }
          if (focusedOptionRef.current !== null) {
            handleSelect(
              filteredOptions[
                focusedOptionRef.current - (showSelectAll ? 1 : 0)
              ],
              true,
            );
          }
          break;

        case ' ':
          e.preventDefault();
          setIsVisible(!isVisible);
          break;

        case 'Escape':
          e.preventDefault();
          setIsVisible(false);

          if (submitRef.current) {
            submitRef.current.cancel();
          }
          break;

        case 'Tab':
          setTimeout(() => {
            setIsVisible(false);
          }, 0);
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          e.preventDefault();
          if (!isVisible) {
            setIsVisible(true);
          }
          setTimeout(() => {
            handleArrowKey(e.key);
          }, 50);
          break;

        default:
          removeFocusClass();
          break;
      }
    },
    [
      showSelectAll,
      focusedOptionRef,
      setIsVisible,
      isVisible,
      submitRef,
      removeFocusClass,
      handleSelectAll,
      filteredOptions,
      selectedOptions,
      handleSelect,
      handleArrowKey,
    ],
  );

  return { handleKeyDown, handleSelect, handleSelectAll, removeFocusClass };
};

export default useKeyNavigation;
